"use client";
import { useState, useRef, useEffect } from "react";
import Image from "next/image";
import { Images } from "@assets/assets";
import Link from "next/link";
import DOMPurify from "isomorphic-dompurify";
import { getYouTubeId, isYouTubeUrl } from "@/lib/Utility";

export default function TestimonialCard({
  companyName,
  // tagline,
  companyLogoSrc,
  ratingBadgeSrc,
  quote,
  authorName,
  authorTitle,
  variant = "white",
  clientVideo,
  surveyId,
  rating = 2, // Defaulting to 2 stars
}: any) {
  const [isHovered, setIsHovered] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const videoRef = useRef<HTMLVideoElement>(null);
  const isGray = variant === "gray";

  useEffect(() => {
    const video = videoRef.current;
    if (video && isHovered && clientVideo) {
      video.currentTime = 0;
      video.play().catch(() => {
        video.muted = true;
        setIsMuted(true);
        video.play();
      });
    } else if (video) {
      video.pause();
    }
  }, [isHovered, clientVideo]);
  const youtubeId = clientVideo ? getYouTubeId(clientVideo) : null;
  const isYoutube = clientVideo ? isYouTubeUrl(clientVideo) : false;
  return (
    <div
      className="w-full h-[550px] relative z-[1] bg-[#225BD4]"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div
        className={`w-full h-full transition-all duration-500 ease-out will-change-transform relative z-[1] border flex flex-col overflow-hidden
          ${isHovered && ratingBadgeSrc ? "rotate-[-2deg] -translate-y-[70px] shadow-[0_20px_60px_rgba(0,0,0,0.3)]" : ""}
          ${isGray ? "bg-[#ECF0F6] border-[#ECF0F6]" : "bg-white border-[#ECF0F6]"}
        `}
      >
        {/* VIDEO LAYER - Only renders and shows if clientVideo exists */}
        {clientVideo && (
          <div
            className={`absolute inset-0 z-[25] bg-black transition-opacity duration-300 ${isHovered ? "opacity-100" : "opacity-0 pointer-events-none"}`}
          >
            {isYoutube && youtubeId ? (
              <iframe
                key="testimonial-video"
                src={`https://www.youtube.com/embed/${youtubeId}?autoplay=1&mute=1&loop=1&playlist=${youtubeId}`}
                title="YouTube video player"
                allow="autoplay; encrypted-media"
                allowFullScreen
                className="w-full h-full object-cover"
              />
            ) : (
              <video
                key="testimonial-video"
                ref={videoRef}
                src={clientVideo}
                playsInline
                preload="metadata"
                loop
                muted={isMuted}
                className="w-full h-full object-cover"
              />
            )}
            <button
              onClick={(e) => {
                e.stopPropagation();
                setIsMuted(!isMuted);
              }}
              aria-label={isMuted ? "Unmute video" : "Mute video"}
              className="absolute top-6 right-6 z-[30] bg-black/40 text-white w-10 h-10 flex items-center justify-center rounded-full backdrop-blur-md"
            >
              {isMuted ? "🔇" : "🔊"}
            </button>
          </div>
        )}

        <div
          className={`flex flex-col h-full transition-opacity duration-300 
          ${isHovered && clientVideo ? "opacity-0" : "opacity-100"}`}
        >
          <div className="w-full p-[42px] pb-0 flex justify-between items-center ">
            <div className="flex gap-[15px]">
              {companyLogoSrc && (
                <div className="w-[78px] h-[78px] relative">
                  <Image
                    src={companyLogoSrc}
                    alt="Company Logo"
                    width={78}
                    height={78}
                    className="rounded-full border bg-white object-cover"
                    sizes="(max-width: 1024px) 100vw, 50vw"
                  />
                </div>
              )}

              <div className="flex flex-col text-left">
                <div className="text-[#00040b] text-[24px] font-medium clash-font">
                  {companyName}
                </div>
                {/* Replaced Category Tag with Stars */}
                <div className="flex gap-0">
                  {[...Array(rating)].map((_, index) => (
                    <svg
                      key={index}
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill={index < rating ? "#FFB400" : "#E2E8F0"}
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
                    </svg>
                  ))}
                </div>
              </div>
            </div>
            {ratingBadgeSrc ? (
              <Image
                src={ratingBadgeSrc}
                alt="rating"
                width={129}
                height={40}
                className="object-contain"
                sizes="(max-width: 1024px) 100vw, 50vw"
              />
            ) : (
              <div className="w-[129px] h-[40px]" />
            )}
          </div>

          {/* Body */}
          <div className="relative flex-grow mt-[30px] px-[42px] pb-[42px] flex flex-col text-left">
            <Image
              src={Images?.testimonial.quote}
              alt="quote"
              width={40}
              height={40}
              className="opacity-40 mb-4"
              sizes="(max-width: 1024px) 100vw, 50vw"
            />
            <p
              className="text-[#636364] text-[1.1rem] italic leading-[160%] line-clamp-7"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(quote) || "" }}
            />
            <div className="mt-auto">
              <div className="text-[#00040b] text-[18px] font-bold">
                {authorName}
              </div>
              <div className="text-[#636364] text-[14px]">{authorTitle}</div>
            </div>
          </div>
        </div>
      </div>

      {surveyId !== 0 && (
        <Link
          href={`/survey/index/${surveyId}`}
          className="absolute bottom-[25px] left-[50%] -translate-x-1/2 text-white font-semibold z-[0] hover:underline cursor-pointer"
        >
          Read Full Review
        </Link>
      )}
    </div>
  );
}